/* =====================================================================================
   Quality — controlled documents.

   The state machine, which the database enforces rather than this file:

     draft -> in_review -> approved -> (superseded when a later version is approved)

   Three rules live in Postgres because an interface cannot be trusted with them:

     * the author of a document may not be its approver (ISO 13485 expects separation, and
       an approval you can give yourself is not an approval)
     * only the NAMED approver may authorise
     * an approval requires a written statement of what is being attested to

   See migration 0016. Everything below just calls those functions and reports what they say.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';
import { explain } from './dberrors';

export interface ControlledDocument {
  id: string;
  doc_number: string;
  title: string;
  doc_type: string;
  status: 'draft' | 'in_review' | 'approved' | 'under_revision' | 'superseded' | 'withdrawn';
  version: number;
  process_area: string | null;
  iso_clauses: string[] | null;
  owner_id: string;
  owner_name?: string;
  approver_id: string | null;
  approver_name?: string;
  approved_at: string | null;
  approval_statement: string | null;
  effective_from: string | null;
  next_review_due: string | null;
  review_period_months: number | null;
  training_required: boolean;
  change_summary: string | null;
}

export interface RegisterRow {
  document_id: string;
  doc_number: string;
  title: string;
  version: number;
  member_id: string;
  full_name: string | null;
  acknowledged_at: string | null;
  outstanding: boolean;
}

const live = () => BACKEND === 'supabase';

export const DOC_TYPES = [
  'quality_manual', 'policy', 'sop', 'work_instruction', 'form', 'template',
  'specification', 'validation_protocol', 'validation_report', 'risk_file',
  'technical_file', 'design_history', 'register', 'other',
] as const;

export async function fetchDocuments(): Promise<ControlledDocument[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('quality').from('controlled_documents')
    .select('*')
    .order('doc_number');
  if (error) throw new Error(explain(error.message, 'save this quality record'));
  const name = await (await import('./supabase')).nameOf();
  return (data ?? []).map((d: any) => ({
    ...d,
    owner_name: name(d.owner_id),
    approver_name: name(d.approver_id),
  }));
}

export async function fetchRegister(): Promise<RegisterRow[]> {
  if (!live()) return (TENANT.seed.qualityDoc ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('quality').from('v_training_register').select('*');
  if (error) throw new Error(explain(error.message, 'save this quality record'));
  return data ?? [];
}

export async function createDocument(d: {
  docNumber: string; title: string; docType: string;
  processArea?: string; trainingRequired: boolean; reviewMonths: number;
  changeSummary?: string;
}) {
  const { supabase, currentMember } = await import('./supabase');
  const m = await currentMember();
  if (!m?.id) throw new Error('No member record for the signed-in user.');
  const { data: org } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();

  const { error } = await supabase().schema('quality').from('controlled_documents').insert({
    org_id: org?.id,
    doc_number: d.docNumber,
    title: d.title,
    doc_type: d.docType,
    process_area: d.processArea || null,
    status: 'draft',
    version: 1,
    owner_id: m.id,
    training_required: d.trainingRequired,
    review_period_months: d.reviewMonths,
    change_summary: d.changeSummary || null,
  });
  if (error) {
    if (error.code === '23505') throw new Error(`Document number ${d.docNumber} already exists.`);
    throw new Error(explain(error.message, 'save this quality record'));
  }
}

/** Ask a named person to authorise. The database refuses if that person is the author. */
export async function submitForApproval(documentId: string, approverId: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('quality').rpc('submit_for_approval', {
    p_document_id: documentId, p_approver_id: approverId,
  });
  if (error) throw new Error(clean(error.message));
}

export async function approveDocument(documentId: string, statement: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('quality').rpc('approve_document', {
    p_document_id: documentId, p_statement: statement,
  });
  if (error) throw new Error(clean(error.message));
}

export async function acknowledgeDocument(documentId: string, signedName: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('quality').rpc('acknowledge_document', {
    p_document_id: documentId, p_signed_name: signedName,
  });
  if (error) throw new Error(clean(error.message));
}

/* PostgREST wraps a raise exception in its own prose. The message the function author wrote
   is the useful part, so strip the wrapper rather than showing the plumbing. */
function clean(message: string): string {
  return message.replace(/^.*?(?:ERROR|error):\s*/i, '').trim() || message;
}

/* =====================================================================================
   Nonconformances and CAPAs.

   The chain the standard expects, and the reason these are three tables rather than one:

     nonconformance   something went wrong
     capa             what we are doing about it, and WHY it happened
     capa_action      the individual tasks, each with an owner and a date

   A CAPA that closes without an effectiveness check is not corrective action, it is
   paperwork. The schema enforces that: `effectiveness_check_due` and a verification date
   are separate from closure, so "we fixed it" and "we confirmed the fix worked" cannot be
   collapsed into the same click. That distinction is the first thing an ISO 13485 auditor
   tests, and the most common thing to get wrong.
   ===================================================================================== */

export const NC_SEVERITY = ['critical', 'major', 'minor', 'observation'] as const;
export const NC_SOURCE = ['internal_audit', 'external_audit', 'complaint', 'supplier',
  'process', 'product', 'software', 'data_incident', 'management_review', 'other'] as const;
export const NC_STATUS = ['open', 'investigating', 'capa_raised', 'corrected',
  'closed', 'not_a_nc'] as const;
export const CAPA_STATUS = ['open', 'root_cause', 'actions_in_progress',
  'awaiting_verification', 'effective', 'ineffective', 'closed'] as const;

export interface Nonconformance {
  id: string; reference: string; title: string; description: string | null;
  source: string; detected_on: string; severity: string; process_area: string | null;
  immediate_action: string | null; containment_done: boolean;
  status: string; closed_on: string | null;
  capas?: { id: string; reference: string; status: string }[];
}

export interface Capa {
  id: string; reference: string; nonconformance_id: string | null;
  title: string; capa_type: string | null;
  problem_statement: string | null; root_cause: string | null; root_cause_method: string | null;
  action_plan: string | null; owner_id: string | null; owner_name?: string;
  raised_on: string; target_close: string | null; status: string;
  effectiveness_check_due: string | null; effectiveness_verified: boolean | null;
  effectiveness_evidence: string | null; effectiveness_verified_on: string | null;
  closed_on: string | null;
  resulting_document_id: string | null;
  actions?: CapaAction[];
}

export interface CapaAction {
  id: string; capa_id: string; description: string;
  assigned_to: string | null; due_on: string | null;
  status: string; completed_on: string | null; evidence: string | null; sort_order: number;
}

async function org(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

export async function fetchNonconformances(): Promise<Nonconformance[]> {
  if (!live()) return (TENANT.seed.nonconformance ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('quality').from('nonconformances')
    .select('*, capas(id, reference, status)')
    .order('detected_on', { ascending: false });
  if (error) throw new Error(explain(error.message, 'save this quality record'));
  return data ?? [];
}

export async function fetchCapas(): Promise<Capa[]> {
  if (!live()) return (TENANT.seed.capa ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('quality').from('capas')
    .select('*, actions:capa_actions(*)')
    .order('raised_on', { ascending: false });
  if (error) throw new Error(explain(error.message, 'save this quality record'));
  const name = await (await import('./supabase')).nameOf();
  return (data ?? []).map((c: any) => ({ ...c, owner_name: name(c.owner_id) }));
}

export async function raiseNonconformance(n: {
  reference: string; title: string; description: string;
  source: string; severity: string; detectedOn: string;
  processArea?: string; immediateAction?: string;
}) {
  // Required by the table, and rightly: a nonconformance nobody described is useless six
  // months later when an auditor asks what actually happened.
  if (n.description.trim().length < 10) {
    throw new Error('Describe what happened. A title alone is not a record.');
  }
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { error } = await supabase().schema('quality').from('nonconformances').insert({
    org_id: await org(), reference: n.reference, title: n.title,
    description: n.description, source: n.source, severity: n.severity,
    detected_on: n.detectedOn, detected_by: me?.id ?? null,
    process_area: n.processArea || null, immediate_action: n.immediateAction || null,
    status: 'open',
  });
  if (error) {
    if (error.code === '23505') throw new Error(`Reference ${n.reference} already exists.`);
    throw new Error(explain(error.message, 'save this quality record'));
  }
}

/**
 * Raise a CAPA against a nonconformance. `effectiveness_check_due` is set at creation
 * rather than at closure: deciding when you will check that a fix worked, before you have
 * done the work, is what stops the check quietly never happening.
 */
export const CAPA_TYPES = ['corrective', 'preventive', 'both'] as const;

export async function raiseCapa(c: {
  reference: string; title: string; nonconformanceId?: string | null;
  capaType: string; problemStatement: string;
  targetClose?: string; effectivenessCheckDue?: string;
}) {
  // Both required by the table. capa_type matters more than it looks: corrective fixes what
  // happened, preventive stops what has not happened yet, and a system that cannot tell them
  // apart cannot show an auditor it does the second one at all.
  if (c.problemStatement.trim().length < 10) {
    throw new Error('State the problem. What is wrong, in a sentence someone else could act on?');
  }
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { error } = await supabase().schema('quality').from('capas').insert({
    org_id: await org(), reference: c.reference, title: c.title,
    nonconformance_id: c.nonconformanceId || null,
    capa_type: c.capaType,
    problem_statement: c.problemStatement,
    owner_id: me?.id ?? null,
    raised_on: new Date().toISOString().slice(0, 10),
    target_close: c.targetClose || null,
    effectiveness_check_due: c.effectivenessCheckDue || null,
    status: 'open',
  });
  if (error) {
    if (error.code === '23505') throw new Error(`Reference ${c.reference} already exists.`);
    throw new Error(explain(error.message, 'save this quality record'));
  }
  if (c.nonconformanceId) {
    await supabase().schema('quality').from('nonconformances')
      .update({ status: 'capa_raised' }).eq('id', c.nonconformanceId);
  }
}

export async function updateCapa(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('quality').from('capas').update(patch).eq('id', id);
  if (error) throw new Error(clean(error.message));
}

export async function updateNonconformance(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('quality').from('nonconformances')
    .update(patch).eq('id', id);
  if (error) throw new Error(clean(error.message));
}

export async function addCapaAction(a: {
  capaId: string; description: string; dueOn?: string; sortOrder?: number;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('quality').from('capa_actions').insert({
    org_id: await org(), capa_id: a.capaId, description: a.description,
    due_on: a.dueOn || null, status: 'open', sort_order: a.sortOrder ?? 0,
  });
  if (error) throw new Error(explain(error.message, 'save this quality record'));
}

export async function updateCapaAction(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  if (patch.status === 'done' && !patch.completed_on) {
    patch.completed_on = new Date().toISOString().slice(0, 10);
  }
  const { error } = await supabase().schema('quality').from('capa_actions')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'save this quality record'));
}

/**
 * Verify that a CAPA actually worked. Deliberately separate from closing it, and requires
 * evidence: "closed" without a verified effectiveness check is the single most common
 * finding in a quality system audit.
 */
export async function verifyEffectiveness(id: string, effective: boolean, evidence: string) {
  if (evidence.trim().length < 10) {
    throw new Error('State the evidence. What did you check, and what did it show?');
  }
  const { currentMember } = await import('./supabase');
  const me = await currentMember();
  await updateCapa(id, {
    effectiveness_verified: effective,
    effectiveness_evidence: evidence,
    effectiveness_verified_by: me?.id ?? null,
    effectiveness_verified_on: new Date().toISOString().slice(0, 10),
    // An ineffective CAPA is NOT closed. It goes back for more work, which is the whole
    // point of checking.
    status: effective ? 'effective' : 'ineffective',
  });
}
